/*
 * Vertrag zwischen selbst gehosteter Instanz und der KaBa-App (iOS/Android).
 * Wird vom Server ausgeliefert (GET /api/mobile/config) und von der App gelesen. Additiv erweitern:
 * neue Felder sind optional, unbekannte Felder werden toleriert, Funktionen nur über `capabilities`.
 */

/** Version des mobilen Vertrags. Erhöhen nur bei inkompatiblen Änderungen. */
export const MOBILE_API_VERSION = 1;

/** Einzige zugelassene Rückkehradresse für die Browser-Anmeldung der App */
export const MOBILE_REDIRECT = 'kaba://auth';

/** Präfix der App-Sitzungen im Authorization-Header (Bearer kbm_…) */
export const MOBILE_TOKEN_PREFIX = 'kbm_';

/** Freigeschaltete Funktionen; die App blendet nicht genannte Funktionen aus */
export const MOBILE_CAPABILITIES = [
  'auth.password',
  'auth.browser',
  'boards',
  'cards',
  'comments',
  'files',
  'library',
  'realtime',
  'collab',
  'offline-ops',
  'inbox',
] as const;
export type MobileCapability = (typeof MOBILE_CAPABILITIES)[number];

export type MobileConfig = {
  product: 'kaba';
  /** Dauerhafte, zufällige Kennung dieser Installation (unabhängig von der Adresse) */
  instanceId: string;
  instanceName: string;
  serverVersion: string;
  apiVersion: number;
  /** Älteste App-Vertragsversion, die dieser Server noch bedient */
  minApiVersion: number;
  /** Adresse, unter der die Instanz eingerichtet ist (KABA_URL); Anmeldung im Browser läuft immer darüber */
  publicUrl: string;
  capabilities: string[];
  auth: {
    password: boolean;
    /** Anmeldung im Systembrowser mit Rückkehr in die App (OIDC, Passkeys, Passwort) */
    browser: boolean;
    oidc: { label: string } | null;
    passkeys: boolean;
    signup: 'open' | 'invite';
  };
  maxUploadMb: number;
  branding: unknown;
};

export type InstanceUrlResult =
  | { ok: true; url: string; host: string }
  | { ok: false; reason: 'empty' | 'invalid' | 'credentials' | 'http' | 'subpath' | 'query' };

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

/**
 * Normalisiert die vom Menschen eingegebene Serveradresse. Ohne Schema wird https angenommen.
 * Abgelehnt: Zugangsdaten in der Adresse, Abfrageteil/Anker, http außer localhost und – in Version 1 –
 * Unterpfade (die Instanz läuft immer im Wurzelpfad, siehe docs/mobile.md).
 */
export function normalizeInstanceUrl(input: string, opts: { allowHttpLocal?: boolean } = {}): InstanceUrlResult {
  const raw = input.trim();
  if (!raw) return { ok: false, reason: 'empty' };
  if (/\s/.test(raw)) return { ok: false, reason: 'invalid' };
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
  let u: URL;
  try {
    u = new URL(withScheme);
  } catch {
    return { ok: false, reason: 'invalid' };
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return { ok: false, reason: 'invalid' };
  if (!u.hostname || u.hostname.endsWith('.')) return { ok: false, reason: 'invalid' };
  if (u.username || u.password || /@/.test(withScheme.split('/')[2] ?? '')) return { ok: false, reason: 'credentials' };
  if (u.search || u.hash || /[?#]/.test(raw)) return { ok: false, reason: 'query' };
  if (u.pathname !== '/' && u.pathname !== '') return { ok: false, reason: 'subpath' };
  if (u.protocol === 'http:' && !(opts.allowHttpLocal && LOCAL_HOSTS.has(u.hostname))) return { ok: false, reason: 'http' };
  return { ok: true, url: u.origin, host: u.host };
}

export const INSTANCE_URL_ERRORS: Record<Exclude<InstanceUrlResult, { ok: true }>['reason'], string> = {
  empty: 'Bitte die Adresse deines KaBa-Servers eingeben.',
  invalid: 'Das ist keine gültige Serveradresse.',
  credentials: 'Die Adresse darf keinen Benutzernamen und kein Passwort enthalten.',
  http: 'Die Verbindung muss verschlüsselt sein (https).',
  subpath: 'KaBa muss direkt unter der Adresse laufen, nicht in einem Unterordner.',
  query: 'Bitte nur die Serveradresse ohne weitere Angaben eingeben.',
};

/** Prüft die Antwort von /api/mobile/config, bevor die App ihr vertraut */
export function checkMobileConfig(data: unknown, appApiVersion = MOBILE_API_VERSION): { ok: true; config: MobileConfig } | { ok: false; reason: 'not-kaba' | 'server-too-old' | 'app-too-old' } {
  const c = data as Partial<MobileConfig> | null;
  if (!c || typeof c !== 'object' || c.product !== 'kaba' || typeof c.instanceId !== 'string' || typeof c.apiVersion !== 'number') {
    return { ok: false, reason: 'not-kaba' };
  }
  if (c.apiVersion < appApiVersion) return { ok: false, reason: 'server-too-old' };
  if (typeof c.minApiVersion === 'number' && c.minApiVersion > appApiVersion) return { ok: false, reason: 'app-too-old' };
  return { ok: true, config: c as MobileConfig };
}

/** S256-Challenge aus dem Verifier (PKCE, RFC 7636) – base64url ohne Auffüllung */
export async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  let s = '';
  for (const b of new Uint8Array(digest)) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export const PKCE_RE = /^[A-Za-z0-9_-]{43,128}$/;
